import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeGrid, type GridChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import type { PhotoListItem } from '@/api/types/photo.types';
import { PhotoCard } from './PhotoCard';

interface PhotoGridProps {
  /** Flat array of all loaded photos across all pages. */
  photos: PhotoListItem[];
  /** Total known items (photos.length if no total count from API). */
  totalCount: number;
  /** Whether more pages can be loaded. */
  hasNextPage: boolean;
  /** Whether a page is currently loading. */
  isLoadingMore: boolean;
  /** Load the next page of results. */
  onLoadMore: () => void;
  /** Selection state */
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  onSelect: (photoId: string) => void;
  /** Open detail view for a photo. */
  onPhotoClick: (photoId: string) => void;
  /** Toggle star on a photo. */
  onToggleStar: (photoId: string, starred: boolean) => void;
}

/** Height of each grid cell (square cards + gap). */
const CELL_SIZE = 192;
const GAP = 8;

/**
 * Virtualised photo grid that supports 4000+ photos efficiently
 * using react-window FixedSizeGrid and react-window-infinite-loader.
 */
export const PhotoGrid = React.memo(function PhotoGrid({
  photos,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
  selectedIds,
  isSelectionMode,
  onSelect,
  onPhotoClick,
  onToggleStar,
}: PhotoGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width for responsive column count
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columnCount = useMemo(() => {
    if (containerWidth < 480) return 2;
    if (containerWidth < 768) return 3;
    if (containerWidth < 1024) return 4;
    if (containerWidth < 1280) return 5;
    return 6;
  }, [containerWidth]);

  const itemCount = hasNextPage ? totalCount + columnCount : totalCount;
  const rowCount = Math.ceil(itemCount / columnCount);

  const isItemLoaded = useCallback(
    (index: number) => index < photos.length,
    [photos.length],
  );

  const loadMoreItems = useCallback(() => {
    if (!isLoadingMore && hasNextPage) {
      onLoadMore();
    }
    // Return a resolved promise for InfiniteLoader
    return Promise.resolve();
  }, [isLoadingMore, hasNextPage, onLoadMore]);

  // Cell renderer for FixedSizeGrid
  const Cell = useCallback(
    ({ columnIndex, rowIndex, style }: GridChildComponentProps) => {
      const photoIndex = rowIndex * columnCount + columnIndex;

      // Pad the style with gap
      const adjustedStyle = {
        ...style,
        left: Number(style.left) + GAP,
        top: Number(style.top) + GAP,
        width: Number(style.width) - GAP,
        height: Number(style.height) - GAP,
      };

      if (photoIndex >= photos.length) {
        // Loading skeleton or empty
        if (hasNextPage && photoIndex < itemCount) {
          return (
            <div style={adjustedStyle}>
              <div className="h-full w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
          );
        }
        return <div style={adjustedStyle} />;
      }

      const photo = photos[photoIndex];

      return (
        <div style={adjustedStyle}>
          <PhotoCard
            photo={photo}
            isSelected={selectedIds.has(photo.id)}
            isSelectionMode={isSelectionMode}
            onSelect={onSelect}
            onClick={onPhotoClick}
            onToggleStar={onToggleStar}
          />
        </div>
      );
    },
    [
      columnCount,
      photos,
      hasNextPage,
      itemCount,
      selectedIds,
      isSelectionMode,
      onSelect,
      onPhotoClick,
      onToggleStar,
    ],
  );

  const cellWidth = containerWidth > 0 ? containerWidth / columnCount : CELL_SIZE;
  const gridHeight = typeof window !== 'undefined' ? window.innerHeight - 240 : 600;

  return (
    <div ref={containerRef} className="w-full">
      {containerWidth > 0 && (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={10}
        >
          {({ onItemsRendered, ref: infiniteRef }) => {
            // Map flat-item indices to grid row indices for InfiniteLoader
            const handleItemsRendered = ({
              visibleRowStartIndex,
              visibleRowStopIndex,
            }: {
              overscanRowStartIndex: number;
              overscanRowStopIndex: number;
              visibleRowStartIndex: number;
              visibleRowStopIndex: number;
            }) => {
              onItemsRendered({
                overscanStartIndex: visibleRowStartIndex * columnCount,
                overscanStopIndex: visibleRowStopIndex * columnCount + columnCount - 1,
                visibleStartIndex: visibleRowStartIndex * columnCount,
                visibleStopIndex: visibleRowStopIndex * columnCount + columnCount - 1,
              });
            };

            return (
              <FixedSizeGrid
                ref={infiniteRef}
                columnCount={columnCount}
                columnWidth={cellWidth}
                rowCount={rowCount}
                rowHeight={cellWidth}
                width={containerWidth}
                height={gridHeight}
                onItemsRendered={handleItemsRendered}
                overscanRowCount={3}
                className="scrollbar-thin"
              >
                {Cell}
              </FixedSizeGrid>
            );
          }}
        </InfiniteLoader>
      )}
    </div>
  );
});
